import { ROWS_BY_KIND } from './resultRows.js';
import { asRecord } from './resultValues.js';
import type {
  CollapsibleField,
  EmptyState,
  PresetCardsField,
  PresetOption,
  WorkflowStep,
} from './workflows.types.js';

export interface EmptyRun {
  // Null when the run's preset is not one the form offers.
  preset: PresetOption | null;
  // An override replaces a preset value, so the preset no longer describes the run.
  customised: boolean;
}

function hasValue(value: unknown): boolean {
  return value !== null && value !== undefined;
}

/**
 * How a completed run whose row list came back empty was configured. The service
 * echoes a run's params onto its result, keyed as the config step's fields are.
 */
export function emptyRunOf(
  result: unknown,
  resultKind: string | null,
  config: WorkflowStep | undefined,
): EmptyRun | null {
  const record = asRecord(result);
  const rowsKey = resultKind === null ? undefined : ROWS_BY_KIND[resultKind];
  const rows = rowsKey === undefined ? undefined : record?.[rowsKey];
  if (record === null || !Array.isArray(rows) || rows.length > 0) return null;

  const fields = config?.fields ?? [];
  const presetField = fields.find(
    (field): field is PresetCardsField => field.kind === 'presetCards',
  );
  const overrides = fields
    .filter((field): field is CollapsibleField => field.kind === 'collapsible')
    .map((field) => asRecord(record[field.key]));
  return {
    preset: presetField?.options.find((option) => option.id === record[presetField.key]) ?? null,
    customised: overrides.some((group) => group !== null && Object.values(group).some(hasValue)),
  };
}

export function fillToken(template: string, token: string, value: string): string {
  return template.split(`{${token}}`).join(value);
}

export function emptyBodyOf(copy: EmptyState, run: EmptyRun): string {
  const standard = run.customised
    ? copy.customStandard
    : run.preset === null
      ? copy.defaultStandard
      : fillToken(copy.presetStandard, 'preset', run.preset.label);
  return fillToken(copy.body, 'standard', standard);
}
